<template>
  <div class="relative z-2">
    <product-list-no-results v-if="(!pending && !products.length) || error" :class="listBrandClasses.noResults" :pickup-filters="false" />
    <component
      :is="isGrid ? BaseGrid : VfCarouselElevate"
      v-else
      ref="gridRef"
      v-bind="options"
      @slides-viewed="pushImpressions"
    >
      <template v-for="(product, i) in products" :key="product.id">
        <div
          v-if="i < maxProductsToShow"
          v-style="{ ...carouselStyle, ...focusedPlacements.get(i)?.style }"
          :class="{
            '<md:hidden': isGrid && i >= effectiveProductsToShow.sm,
            '~md:hidden': isGrid && i >= effectiveProductsToShow.md,
            'lg:hidden': isGrid && i >= effectiveProductsToShow.lg,
          }"
          :role="!isGrid ? 'tabpanel' : undefined"
        >
          <component
            :is="isGrid ? BaseLazyHydrate : BaseWrapper"
            :interaction="['mouseenter', 'focus', 'touchstart']"
            :when="hydrationMode"
          >
            <component
              :is="getCardComponent(product)"
              v-bind="getCardOptions(product, i)"
              @product-click="handleProductClick(product, i + 1)"
            />
          </component>
        </div>
      </template>
      <div
        v-for="section in inlineSectionMap"
        :key="section.name"
        v-style="{
          'grid-col': section.column,
          'grid-row': section.row,
          'display': section.display,
        }"
        class="relative"
        :class="[plpBrandClasses.eSpotInlineGridWrapper]"
      >
        <lazy-cms-section
          :class="[getInGridEspotStyle(options.cols!, section.width), { invisible: showEspotSkeleton }]"
          full-height
          :section
        />
        <div v-if="showEspotSkeleton" class="absolute-0 z-overlay skeleton" />
      </div>
      <template v-if="pending">
        <product-card-skeleton
          v-for="i in (isGrid ? (nextPageLoading ? maxNextPageSize : maxProductsToShow) : maxProductsToShow)"
          :key="`product-${i}`"
          v-style="carouselStyle"
          :class="{
            '<md:hidden': isGrid && i > (nextPageLoading
              ? (effectiveProductsToShow.sm - products.length)
              : effectiveProductsToShow.sm),
            '~md:hidden': isGrid && i > (nextPageLoading
              ? (effectiveProductsToShow.md - products.length)
              : effectiveProductsToShow.md),
            'lg:hidden': isGrid && i > (nextPageLoading
              ? (effectiveProductsToShow.lg - products.length)
              : effectiveProductsToShow.lg),
          }"
          :show-swatches="$feature.showSwatchesInProductList"
        />
      </template>
      <template
        v-if="!isGrid && carouselElevatedControls"
        #pagination="{ activeItem, total: totalCarouselItems, scrollTo }"
      >
        <template v-if="products.length && gridRef?.canScroll">
          <vf-carousel-pagination-dots
            :active="activeItem"
            class="justify-center"
            :total="totalCarouselItems"
            @update:active="scrollTo($event)"
          />
        </template>
      </template>
    </component>

    <template v-if="isGrid && gridItem.shopAll === 'pagination'">
      <product-list-pagination-skeleton v-if="pending" />
      <template v-else-if="products.length">
        <product-list-pagination
          class="hidden <md:block"
          :class="listBrandClasses.pagination"
          :current="Math.min(products.length, effectiveProductsToShow.sm)"
          no-link
          :page-size="nextPageSize.sm"
          :total
          @load-more="loadMore"
        />
        <product-list-pagination
          class="hidden ~md:block"
          :class="listBrandClasses.pagination"
          :current="Math.min(products.length, effectiveProductsToShow.md)"
          no-link
          :page-size="nextPageSize.md"
          :total
          @load-more="loadMore"
        />
        <product-list-pagination
          class="hidden lg:block lg:flex"
          :class="listBrandClasses.pagination"
          :current="Math.min(products.length, effectiveProductsToShow.lg)"
          no-link
          :page-size="nextPageSize.lg"
          :total
          @load-more="loadMore"
        />
      </template>
    </template>
    <div
      v-else-if="ctaTarget"
      v-style="brandStyles.container"
      :class="[listBrandClasses.pagination, paginationBrandClasses.container]"
    >
      <cms-shared-button
        :size="ctaSize"
        :style="ctaStyle"
        v-bind="{ ...ctaTarget, ...textLinkStyles }"
      />
    </div>
  </div>
</template>

<script lang="ts" setup>
import type { BaseGrid as BaseGridType } from '#components'
import type { ProductBreadcrumbs, ProductCardExtended } from '#root/api/clients/product/data-contracts'
import type { Responsive } from '#types/common'
import type { FilterByContent } from '#types/components/cms/filter-by'
import type { ProductGridItem } from '#types/components/cms/product-grid'
import type { FocusedProductPlacement } from '#types/components/product/list'
import type { ButtonVariant } from '#types/components/vf/button'
import type { SortType } from '#types/config/pages/plp'
import type { Content } from '#types/content'
import type { TextLinkStyle } from '#types/styles'
import { BaseGrid, BaseLazyHydrate, BaseWrapper, ProductCard, ProductCardRecommended, VfCarouselElevate } from '#components'
import mappings from '#content/mappings'

const {
  carouselGaps,
  carouselStyle,
  colsLg,
  ctaStyle,
  edgeToEdge,
  enableLoop,
  gridItem,
  initialProductsToShow,
  isGrid,
  moreProductsToShow,
  showFocusedProduct,
  title,
  textLinkStyles,
  ssrKey,
  variant
} = defineProps<{
  carouselGaps: Responsive
  carouselStyle?: { px: Partial<Responsive>, w: Responsive }
  colsLg: number
  ctaStyle: Responsive<ButtonVariant>
  edgeToEdge?: boolean
  enableLoop?: boolean
  gridItem: ProductGridItem
  initialProductsToShow: Required<Omit<Responsive<number>, 'md'>>
  isGrid: boolean
  moreProductsToShow: Required<Omit<Responsive<number>, 'md'>>
  showFocusedProduct: boolean
  title?: string
  textLinkStyles: TextLinkStyle
  ssrKey: string
  variant?: string
}>()

provide('disableGallery', !isGrid)

const { $feature, $gtm, $t, $viewport } = useNuxtApp()
const { handleBloomreachProductClick } = useBloomreach()
const { currency } = useRuntimeConfig().public
const locale = useLocale()
const route = useRoute()
const pdpData = usePdp()

const appConfig = useAppConfig()
const { brandClasses: plpBrandClasses, defaultSort } = appConfig.pages.plp
const {
  list: {
    brandClasses: listBrandClasses,
    grid,
    hydrationMode,
    productsPerPage,
  },
  pagination: {
    brandClasses: paginationBrandClasses,
    brandStyles,
    buttonSize,
  }
} = appConfig.components.product
const {
  brandClasses: { carouselArrowsClasses, carouselArrowsOffsetClasses },
  carouselControlsSize,
  carouselElevatedControls
} = appConfig.components.cms.productGrid

const is = (type: ProductGridItem['gridType']) => gridItem.gridType === type

const { buttonSize: ctaSize } = mapObject({ buttonSize }, (v) => isObject(v)
  ? mapObject(v, (x) => ({ size: x }))
  : { sm: { size: v } })

const carouselBreakpoints = [
  { bp: 'sm', cls: '<md:-mx-$mx', cssVar: '--mx' },
  { bp: 'md', cls: '~md:-mx-$mx-md', cssVar: '--mx-md' },
  { bp: 'lg', cls: 'lg:-mx-$mx-lg', cssVar: '--mx-lg' },
] as const

const options = isGrid
  ? {
      cols: { sm: 2, lg: colsLg },
      gapX: grid.gaps.x,
      gapY: is('content-cards') ? grid.gaps.x : grid.gaps.y
    }
  : {
      align: 'start',
      bleed: edgeToEdge,
      dragFree: false,
      // Loop recycling teleports slides at the embla viewport boundary, which is visible in the unclipped bleed area
      loop: !edgeToEdge && enableLoop,
      showArrows: !carouselElevatedControls,
      showIndicators: carouselElevatedControls,
      classContainer: carouselBreakpoints.map(({ bp, cls }) => getValueForBreakpoint(bp, carouselGaps) ? cls : ''),
      styleContainer: Object.fromEntries(
        carouselBreakpoints.flatMap(({ bp, cssVar }) => {
          const val = getValueForBreakpoint(bp, carouselGaps)
          return val ? [[cssVar, `${val}`]] : []
        })
      ),
      classControls: [carouselArrowsClasses, carouselArrowsOffsetClasses?.[gridItem.gridType]],
      sizeControls: carouselControlsSize
    }

const error = ref(false)
const start = ref(0)
const additionalData = useState<{
  trackingCategory?: string
  breadcrumbs?: ProductBreadcrumbs
  categoryData?: {
    title?: string
    url?: string
  }
}>(`${ssrKey}-additional`, () => ({}))
const products = useState<(ProductCardExtended | Content)[]>(`${ssrKey}-products`, () => [])
const total = useState<number>(`${ssrKey}-total`, () => 0)
const productsToShow = ref({
  sm: initialProductsToShow.sm,
  lg: initialProductsToShow.lg
})
const lastImpressedCount = ref(0)

const gridRef = useTemplateRef<typeof BaseGridType>('gridRef')

const isVisible = useElementVisibility(computed(() => gridRef.value?.$el))

const pending = ref(is('bloomreach'))
const maxProductsToShow = computed(() => Math.max(...Object.values(productsToShow.value)))
const nextPageLoading = computed(() => pending.value && products.value.length)
const hasCartFilterEspot = (gridItem.espot ?? []).some(
  (item) => item.type === 'VfCanvasFilterBy' && hasByCartFilter((item as unknown as FilterByContent).filters)
)
const { cartItems: resolvedCartItems, showSkeleton: showEspotSkeleton } = useSsrSafeFilterState(hasCartFilterEspot)
const inlineSectionMap = computed(
  () => isGrid
    ? getFilteredEspots(
        gridItem.espot || [],
        maxProductsToShow.value,
        options.cols!,
        { route, attributes: pdpData?.product.value?.contentAttributes, cartItems: resolvedCartItems.value }
      )
    : []
)
// `?spotlight=<productId>` drives eSpot items flagged `poweredBySpotlightParameter`.
const spotlightProductId = computed(() => {
  const value = route.query.spotlight
  return (Array.isArray(value) ? value[0] : value)?.trim() || undefined
})
// Only a category grid renders catalog cards, and only a grid variant has cells to place them in -
// in a carousel every card is one slide, so there is nothing to span.
const focusedPlacements = computed(() =>
  isGrid && is('category') && gridItem.focusedProductEspot?.length
    ? resolveFocusedProducts(
        gridItem.focusedProductEspot,
        products.value.slice(0, maxProductsToShow.value) as ProductCardExtended[],
        options.cols!,
        productsPerPage,
        spotlightProductId.value,
        // Transition-only: legacy focused cards still take 2 cells at sm.
        (product) => showFocusedProduct && !!product.focusedProduct
      )
    : new Map<number, FocusedProductPlacement>()
)
// Cells a featured card takes on top of the 1x1 card it replaces, counted like an in-grid eSpot's
// so the grid keeps the row count its variant authored.
const focusedSectionSize = (breakpoint: keyof Responsive, columns: number) => {
  const extra = Math.min(FOCUSED_PRODUCT_SPAN, columns) * FOCUSED_PRODUCT_SPAN - 1
  const featured = [...focusedPlacements.value.values()]
    .filter(({ focused }) => focused[breakpoint])
  return featured.length * extra
}
const inlineSectionSizes = computed(() =>
  inlineSectionMap.value.reduce(
    (acc, section) => ({
      sm: acc.sm + (section.display.sm === 'block' ? section.size.sm : 0),
      md: (acc.md) + (section.display.md === 'block' ? section.size.md ?? 0 : 0),
      lg: (acc.lg) + (section.display.lg === 'block' ? section.size.lg ?? 0 : 0),
    }),
    { sm: 0, md: 0, lg: 0 }
  )
)
const effectiveProductsToShow = computed(() => {
  if (!isGrid) return { sm: productsToShow.value.sm, md: productsToShow.value.sm, lg: productsToShow.value.lg }
  const calc = (pShow: number, sizes: number, cols: number) =>
    sizes > 0 ? Math.min(pShow, Math.floor((pShow + sizes) / cols) * cols - sizes) : pShow
  return {
    sm: calc(productsToShow.value.sm, (inlineSectionSizes.value.sm ?? 0) + focusedSectionSize('sm', 2), 2),
    md: calc(productsToShow.value.sm, (inlineSectionSizes.value.md ?? 0) + focusedSectionSize('md', 2), 2),
    lg: calc(productsToShow.value.lg, (inlineSectionSizes.value.lg ?? 0) + focusedSectionSize('lg', colsLg), colsLg),
  }
})
const nextPageSize = computed(() => ({
  sm: Math.min(
    moreProductsToShow.sm,
    total.value - effectiveProductsToShow.value.sm
  ),
  md: Math.min(
    moreProductsToShow.sm,
    total.value - effectiveProductsToShow.value.md
  ),
  lg: Math.min(
    moreProductsToShow.lg,
    total.value - effectiveProductsToShow.value.lg
  )
}))
const maxNextPageSize = computed(() => Math.abs(Math.max(...Object.values(nextPageSize.value))))
const ctaTarget = computed(() => {
  if (gridItem.shopAll !== 'shopAll') return
  if (gridItem.target) return gridItem.target
  if (!is('category')) return

  return {
    ariaLabel: `${$t.shopNow} - ${additionalData.value.categoryData?.title}`,
    text: $t.shopNow,
    url: additionalData.value.categoryData?.url
  }
})
const gtmData = computed(() => {
  const pageType = isClient() ? extractPageType(route.path) : ''

  return {
    category: additionalData.value.trackingCategory || `${pageType} | Product Grid Component: ${title} | ${gridItem.title}`,
    list: `${pageType} | Product Grid Component: ${title} | ${gridItem.title}`,
  }
})

const pushImpressions = (indexes?: number[]) => {
  if (isServer() || is('content-cards')) return
  if (isGrid && !isVisible.value) return

  let items: ProductCardExtended[]
  let offset: number

  if (isGrid) {
    const from = lastImpressedCount.value
    const to = effectiveProductsToShow.value[$viewport.breakpoint]
    if (to <= from) return

    items = (products.value as ProductCardExtended[]).slice(from, to)
    offset = from
    lastImpressedCount.value = from + items.length
  }
  else {
    items = (indexes?.map((index) => products.value[index]).filter(Boolean) || []) as ProductCardExtended[]
    offset = indexes?.[0] || 0
  }

  if (items.length) {
    setInteractionOrigin('product-grid')
    $gtm.push('product.onProductGridImpression', items, { ...gtmData.value, offset })
  }
}

const loadHandlers: Record<ProductGridItem['gridType'], () => void | Promise<void>> = {
  'category': async () => {
    const isPdpFallback = !gridItem.categoryId
    const cgid = isPdpFallback ? getUrlId(route.path, 'ProductCategory') : gridItem.categoryId
    if (!cgid) return

    try {
      pending.value = true

      const {
        filters = '',
        sort,
        storeFilter,
        customTrigger,
        ...query
      } = Object.fromEntries(new URLSearchParams(gridItem.categoryFilter).entries())

      const response = await useApi().products.$catalog({
        cgid,
        sort: (sort as SortType) || defaultSort,
        start: start.value,
        count: productsPerPage,
        filters: getFiltersQueryString(filters),
        storeFilter,
        customTrigger,
        ...getTrackingQueryParams(query),
      })

      if (!products.value.length) {
        total.value = response.total
        additionalData.value = {
          trackingCategory: formatTrackingCategory(
            getProductMetaTagStringValue(response.pageMetaData?.defaultPageName),
            response.url
          ),
          breadcrumbs: response.breadcrumbs,
          categoryData: {
            title: response.title,
            url: response.url
          }
        }
      }

      const fetched = isPdpFallback
        ? response.products.filter(({ id }) => id !== getUrlId(route.path, 'Product'))
        : response.products

      total.value -= response.products.length - fetched.length

      products.value = [...products.value, ...fetched]
      pushImpressions()
    }
    catch {
      error.value = true
    }
    finally {
      pending.value = false
    }
  },
  'bloomreach': () => {
    pending.value = true
    products.value = (window.vfbr?.[gridItem?.bloomreachIndex || 0] || [])
      .map((product) => extractBloomReachProduct(product, $feature.showRatingOnPLP))
    total.value = products.value.length
    pending.value = false
    lastImpressedCount.value = 0
    pushImpressions()
  },
  'content-cards': () => {
    products.value = gridItem.contentCards || []
    total.value = products.value.length
  },
}

const loadData = () => loadHandlers[gridItem.gridType]?.()

const loadMore = () => {
  const lastCard = gridRef.value?.$el.lastElementChild.querySelector('[data-test-id="product-card-add"]')
  lastCard?.focus()
  productsToShow.value = {
    sm: productsToShow.value.sm + nextPageSize.value.sm,
    lg: productsToShow.value.lg + nextPageSize.value.lg
  }
  if (maxProductsToShow.value > products.value.length) {
    start.value += productsPerPage
    loadData()
  }
  pushImpressions()
}

const handleProductClick = (product: any, position: number) => {
  if (is('bloomreach')) {
    handleBloomreachProductClick(product, position, title, route.path)
  }
  else {
    setGtmCartProductsMap(product.id, { ...product, ...gtmData.value }, true)
  }
}

const cardOptionsMap: Record<ProductGridItem['gridType'], (product: any, i: number) => object> = {
  'category': (product, i) => ({
    assetOverride: focusedPlacements.value.get(i)?.assetOverride,
    breadcrumbs: additionalData.value.breadcrumbs,
    focused: focusedPlacements.value.get(i)?.focused,
    class: { '<md:col-span-2': showFocusedProduct && product.focusedProduct && !focusedPlacements.value.has(i) },
    hideRating: !$feature.showRatingOnPLP,
    imageFetchPriority: i < colsLg ? 'high' : 'auto',
    lazy: i >= colsLg,
    product,
    showDiscountPercentage: $feature.showDiscountPercentageOnCatalog,
    showSwatches: $feature.showSwatchesInProductList,
    trackingCategory: gtmData.value.category,
    ...(isClient() && { trackingList: gtmData.value.list }),
    trackingOffset: i,
  }),
  'bloomreach': (product) => ({
    ...product,
    ...(isClient() && { trackingList: gtmData.value.list }),
    currency: currency[locale],
    quickshop: true,
    gtmConfig: {},
    suppressProductClickGtm: true,
    variant
  }),
  'content-cards': (product) => ({
    content: product,
    index: products.value.indexOf(product),
    isPartOfCollection: true,
  }),
}

const getCardComponent = (product: any) => {
  if (is('content-cards'))
    return `lazy-cms-${resolveContentComponent(product, mappings)?.componentType}`
  return is('category') ? ProductCard : ProductCardRecommended
}

const getCardOptions = (product: any, i: number) => cardOptionsMap[gridItem.gridType]?.(product, i)

if (isServer() && (!is('bloomreach')))
  await loadData()

if (isGrid) whenever(isVisible, () => pushImpressions())

onMounted(() => {
  if (!products.value.length) {
    loadData()
  }
  else {
    pushImpressions()
  }

  if (is('bloomreach'))
    window.addEventListener('recalculateBloomreachVfa', loadData)
})

onBeforeUnmount(() => {
  window.removeEventListener('recalculateBloomreachVfa', loadData)
  clearNuxtState([`${ssrKey}-products`, `${ssrKey}-additional`, `${ssrKey}-total`])
})
</script>
